"use client";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useRouter } from "next/navigation";
import { graphQlFetchFunction } from "@packages/lib/server-actions/server-action";
import { GA4DataLayer } from "@packages/lib/utlils/helper-function";
import { DataLayerGA4AttrType } from "@packages/lib/types/datalayerGA";
import { GAConstants } from "@packages/shared-components/services/constants";

interface Article {
  pageTitle: string;
  urlSlug: string;
  shortDescription?: string;
}

interface AdviceTabProps {
  customGA?: DataLayerGA4AttrType;
  searchFormHandle: any;
  setsearchFormHandle: any;
  placeholder: any;
}

const SEARCH_LIMIT = 20;
const MIN_CHARS = 3;

const CONTENTFUL_SPACE_ID = process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID;
const CONTENTFUL_ENV = process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT;
const CONTENTFUL_CMA_TOKEN = process.env.NEXT_PUBLIC_CONTENTFUL_CMA_TOKEN;

const highlightMatch = (text: string, query: string) => {
  if (!query || query.length < MIN_CHARS) return <>{text}</>;
  const regex = new RegExp(
    `(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "i",
  );
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <span key={i} className="font-bold text-blue-500">
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
};

// Keyword search via GraphQL (fallback)
const articleSearchQuery = (searchTerm: string) => `{
  articleCollection(
    limit: ${SEARCH_LIMIT}
    where: {
      pageTitle_contains: "${searchTerm}"
      website: { websiteName: "${process.env.NEXT_PUBLIC_PROJECT || process.env.PROJECT}" }
    }
    order: pageTitle_ASC
  ) {
    items {
      pageTitle
      urlSlug
      shortDescription
    }
  }
}`;

// Semantic search via Contentful CMA API
async function semanticSearch(query: string): Promise<Article[]> {
  try {
    // Step 1: Get entry IDs from semantic search
    const response = await fetch(
      `https://api.contentful.com/spaces/${CONTENTFUL_SPACE_ID}/environments/${CONTENTFUL_ENV}/semantic/search`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${CONTENTFUL_CMA_TOKEN}`,
          "Content-Type": "application/vnd.contentful.management.v1+json",
        },
        body: JSON.stringify({
          query,
          filter: {
            entityType: "Entry",
            contentTypeIds: ["article"],
          },
        }),
      },
    );

    if (!response.ok) {
      console.warn(
        "[SemanticSearch] Failed:",
        response.status,
        await response.text(),
      );
      return [];
    }

    const data = await response.json();
    console.log(
      "[SemanticSearch] Raw response:",
      JSON.stringify(data, null, 2),
    );

    // Extract entry IDs from semantic results
    const entryIds: string[] = (data?.items || [])
      .map((item: any) => item?.sys?.entity?.sys?.id)
      .filter(Boolean);

    console.log("[SemanticSearch] Entry IDs found:", entryIds);

    if (entryIds.length === 0) return [];

    // Step 2: Fetch full entry details using CDA GraphQL
    const idsFilter = entryIds.map((id) => `"${id}"`).join(", ");
    const entryQuery = `{
      articleCollection(
        where: { sys: { id_in: [${idsFilter}] } }
        limit: ${entryIds.length}
      ) {
        items {
          sys { id }
          pageTitle
          urlSlug
          shortDescription
        }
      }
    }`;

    const entryData = await graphQlFetchFunction(entryQuery, false);
    const articles = entryData?.data?.articleCollection?.items || [];
    console.log("[SemanticSearch] Fetched article details:", articles);

    // Maintain semantic search order (by relevance)
    const articleMap = new Map(articles.map((a: any) => [a.sys?.id, a]));
    const orderedResults: Article[] = entryIds
      .map((id) => articleMap.get(id))
      .filter(Boolean)
      .map((item: any) => ({
        pageTitle: item.pageTitle || "",
        urlSlug: item.urlSlug || "",
        shortDescription: item.shortDescription || "",
      }));

    console.log("[SemanticSearch] Final ordered results:", orderedResults);
    return orderedResults;
  } catch (error) {
    console.warn("[SemanticSearch] Error:", error);
    return [];
  }
}

const DropdownSkeleton = () => (
  <div className="flex flex-col gap-[4px] p-[8px]">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="px-[16px] py-[12px]">
        <div
          className="skeleton skeleton-text-animated h-[16px] rounded-[4px]"
          style={{ width: `${70 + Math.random() * 25}%` }}
        />
      </div>
    ))}
  </div>
);

const AdviceTab: React.FC<AdviceTabProps> = ({
  customGA,
  searchFormHandle,
  setsearchFormHandle,
  placeholder,
}) => {
  const router = useRouter();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const [searchResults, setSearchResults] = useState<Article[]>([]);
  const [dropdown, setDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchMode, setSearchMode] = useState<"semantic" | "keyword">(
    "semantic",
  );
  const [error, setError] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef<HTMLUListElement | null>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchArticles = useCallback(async (term: string) => {
    setIsSearching(true);
    setSearchResults([]);
    console.log("[AdviceSearch] Searching for:", term);

    // Try semantic search first, fall back to keyword search
    if (CONTENTFUL_CMA_TOKEN) {
      console.log("[AdviceSearch] Trying semantic search...");
      const semanticResults = await semanticSearch(term);
      if (semanticResults.length > 0) {
        console.log(
          "[AdviceSearch] Semantic search returned:",
          semanticResults.length,
          "results",
        );
        setSearchResults(semanticResults);
        setSearchMode("semantic");
        setIsSearching(false);
        return;
      }
      console.log(
        "[AdviceSearch] Semantic search returned 0 results, falling back to keyword",
      );
    } else {
      console.log("[AdviceSearch] No CMA token, skipping semantic search");
    }

    // Fallback: keyword search via GraphQL
    console.log("[AdviceSearch] Using keyword search (GraphQL)...");
    const data = await graphQlFetchFunction(articleSearchQuery(term), false);
    const items = data?.data?.articleCollection?.items || [];
    console.log("[KeywordSearch] GraphQL response:", items);
    console.log("[KeywordSearch] Results count:", items.length);
    setSearchResults(items);
    setSearchMode("keyword");
    setIsSearching(false);
  }, []);

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(-1);
  }, [searchResults]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!dropdown || searchResults.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev < searchResults.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev > 0 ? prev - 1 : searchResults.length - 1
      );
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      const item = searchResults[activeIndex];
      setsearchFormHandle((prev: any) => ({
        ...prev,
        advice: item.pageTitle,
      }));
      setDropdown(false);
      setError("");
      GA4DataLayer({
        event: GAConstants.track_event,
        event_name: "search_event",
        search_type: searchMode === "semantic" ? "semantic_search" : "article_search",
      });
      router.push(item.urlSlug);
    } else if (e.key === "Escape") {
      setDropdown(false);
      setActiveIndex(-1);
    }
  };

  // Auto-scroll active item into view
  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const activeItem = listRef.current.children[activeIndex] as HTMLElement;
      activeItem?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s{2,}/g, " ").trimStart();
    setsearchFormHandle((prev: any) => ({ ...prev, advice: value }));
    setError("");
    setDropdown(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (value.length >= MIN_CHARS) {
      setIsSearching(true);
      debounceRef.current = setTimeout(() => {
        fetchArticles(value);
      }, 1300);
    } else {
      setIsSearching(false);
      setSearchResults([]);
    }
  };

  const searchText = searchFormHandle?.advice || "";

  return (
    <div className="flex flex-col gap-[16px]">
      <div
        ref={containerRef}
        className="bg-white rounded-[24px] p-[16px] border border-grey-200 hover:border-primary-500 shadow-custom-1 md:rounded-[32px] md:pl-[24px] md:p-[10px]"
      >
        <div className="flex flex-col small md:flex-row md:items-center">
          <div className="w-full relative grow">
            <input
              autoComplete="off"
              name="keyword"
              data-text-id="advice_search_input"
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              type="text"
              className="w-full focus:outline-none text-black placeholder:text-gray-500 px-[0] py-[12px] md:px-[16px] md:py-[10px]"
              aria-label="Search articles"
              role="combobox"
              aria-expanded={dropdown && searchText.length >= MIN_CHARS}
              aria-activedescendant={activeIndex >= 0 ? `advice-item-${activeIndex}` : undefined}
              placeholder={placeholder || "Search articles"}
              value={searchText}
              onClick={() => {
                setDropdown(true);
                setError("");
              }}
            />
            {dropdown && searchText.length >= MIN_CHARS && (
              <div className="bg-white z-[1] shadow-custom-3 rounded-[4px] absolute left-0 top-full w-full max-h-[310px] overflow-y-auto custom-scrollbar-2">
                {isSearching ? (
                  <DropdownSkeleton />
                ) : searchResults.length > 0 ? (
                  <>
                    <div className="px-[16px] py-[8px] sticky top-0 bg-white flex items-center justify-between">
                      {/* <span className="bg-secondary-50 text-blue-500 whitespace-nowrap rounded-[4px] px-[10px] py-[3px] font-semibold x-small inline-block">
                        Please select from below
                      </span> */}
                    </div>
                    <ul ref={listRef} role="listbox">
                      {searchResults.map((item, index) => (
                        <li
                          key={index}
                          id={`advice-item-${index}`}
                          role="option"
                          aria-selected={index === activeIndex}
                          onClick={() => {
                            setsearchFormHandle((prev: any) => ({
                              ...prev,
                              advice: item.pageTitle,
                            }));
                            setDropdown(false);
                            setError("");
                            GA4DataLayer({
                              event: GAConstants.track_event,
                              event_name: "search_event",
                              search_type:
                                searchMode === "semantic"
                                  ? "semantic_search"
                                  : "article_search",
                            });
                            router.push(item.urlSlug);
                          }}
                          className={`block small px-[16px] py-[12px] hover:bg-blue-50 hover:underline cursor-pointer ${index === activeIndex ? "bg-blue-50 underline" : ""}`}
                        >
                          <span className="line-clamp-1">
                            {highlightMatch(item.pageTitle, searchText)}
                          </span>
                          {searchMode === "semantic" &&
                            item.shortDescription && (
                              <span className="text-grey-500 x-small line-clamp-1 mt-[2px] block">
                                {item.shortDescription}
                              </span>
                            )}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="px-[16px] py-[12px] small text-gray-500">
                    No results found
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {error && <p className="small text-negative-default">{error}</p>}
    </div>
  );
};

export default AdviceTab;
